import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/common/button';
import { Checkbox } from '@/ui/common/checkbox';
import { Icon } from '@/ui/common/icon';

export interface BetslipSuccessProps {
  ticketNumber: string;
  placedAt: string;
  tips: number;
  stakeLabel: string;
  maxOddsLabel: string;
  possibleWinLabel: string;
  /** Display name of the FreeBet / BackupBet used on this ticket, if any. */
  rewardName?: string | null;
  keepBets: boolean;
  onKeepBetsChange: (keep: boolean) => void;
  /** Flattened selection / custom rows for the More expander. */
  selectionList?: ReactNode;
  onContinue: () => void;
  className?: string;
}

/**
 * Ticket Success after a successful place — Figma Betslip / Ticket Success
 * (summary + More selections + Keep bets + Continue).
 */
export function BetslipSuccess({
  ticketNumber,
  placedAt,
  tips,
  stakeLabel,
  maxOddsLabel,
  possibleWinLabel,
  rewardName = null,
  keepBets,
  onKeepBetsChange,
  selectionList,
  onContinue,
  className,
}: BetslipSuccessProps) {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const iconSize = moreOpen ? 40 : 56;
  const usedRewardName = rewardName?.trim() || null;

  const summaryRows: { label: string; value: string; bold?: boolean }[] = [
    {
      label: t('NumberOfBets', { defaultValue: 'Number of bets' }),
      value: String(tips),
    },
    {
      label: t('Stake', { defaultValue: 'Stake' }),
      value: stakeLabel,
      bold: true,
    },
    {
      label: t('MaxOdds', { defaultValue: 'Max. Odds' }),
      value: maxOddsLabel,
    },
    {
      label: t('PossibleWin', { defaultValue: 'Possible Win' }),
      value: possibleWinLabel,
      bold: true,
    },
    {
      label: t('Date', { defaultValue: 'Date' }),
      value: placedAt,
    },
    ...(usedRewardName
      ? [
          {
            label: t('Reward', { defaultValue: 'Reward' }),
            value: usedRewardName,
            bold: true,
          },
        ]
      : []),
  ];

  return (
    <div
      role="status"
      className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', className)}
    >
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col items-center overflow-y-auto overscroll-contain',
          moreOpen ? 'gap-3 px-3 pb-3 pt-4' : 'gap-4 px-4 pb-4 pt-8',
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col items-center',
            moreOpen ? 'gap-2' : 'gap-4',
          )}
        >
          <Icon
            name="betslip-success"
            size={iconSize}
            className="shrink-0 text-status-success"
          />
          <p
            className={cn(
              'text-center font-bold text-text-primary',
              moreOpen ? 'text-base leading-[22px]' : 'text-lg leading-6',
            )}
          >
            {t('TicketSuccess', { defaultValue: 'Bet placed successfully' })}
          </p>
          {ticketNumber ? (
            <div
              className={cn(
                'flex items-center justify-center gap-3 text-[13px]',
                moreOpen && 'gap-2 text-body-s leading-4',
              )}
            >
              <span className="text-text-secondary">
                {t('TicketNumber', { defaultValue: 'Ticket number' })}:
              </span>
              <span className="font-bold text-text-primary">{ticketNumber}</span>
            </div>
          ) : null}
        </div>

        <dl className="flex w-full flex-col gap-2 pt-3 text-[13px] leading-[18px]">
          {summaryRows.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-2 overflow-hidden">
              <dt className="shrink-0 text-text-secondary">{row.label}</dt>
              <dd
                className={cn(
                  'min-w-0 flex-1 text-right tabular-nums text-text-primary',
                  row.bold && 'font-bold',
                )}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>

        <Checkbox
          name="KeepBets"
          checked={keepBets}
          onChange={(event) => onKeepBetsChange(event.target.checked)}
          label={t('KeepPlacedBetsInBetSlip', {
            defaultValue: 'Keep placed bets in bet slip',
          })}
          className="w-full"
        />

        <button
          type="button"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((open) => !open)}
          className="flex w-full items-center justify-center gap-2 py-2 text-[13px] leading-[18px] text-text-primary"
        >
          <span>{t('More', { defaultValue: 'More' })}</span>
          <Icon name={moreOpen ? 'chevron-up' : 'chevron-down'} size={16} />
        </button>

        {moreOpen && selectionList ? (
          <div className="flex w-full flex-col gap-2">{selectionList}</div>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col px-4 pt-3 pb-4">
        <Button size="lg" fullWidth onClick={onContinue}>
          {t('ContinueBetting', { defaultValue: 'Continue betting' })}
        </Button>
      </div>
    </div>
  );
}
